import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle,
  Users,
  Cpu,
  Cloud,
  BarChart3,
  Settings,
  Zap,
  Target,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHeader from '../components/layout/PageHeader';

const IMAGES = {
  staffing: "https://images.unsplash.com/photo-1752159684779-0639174cdfac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMHNoYWtpbmclMjBoYW5kcyUyMGRlYWx8ZW58MHx8fHwxNzczNzc1OTMzfDA&ixlib=rb-4.1.0&q=85",
  ai: "https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwbmV1cmFsJTIwbmV0d29yayUyMGFic3RyYWN0JTIwYmx1ZSUyMHRlY2h8ZW58MHx8fHwxNzczNzc1OTMyfDA&ixlib=rb-4.1.0&q=85",
  cloud: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  consulting: "https://images.unsplash.com/photo-1565351167686-7a19c5114965?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMHdoaXRlYm9hcmQlMjBzdHJhdGVneXxlbnwwfHx8fDE3NzM3NzU5MzR8MA&ixlib=rb-4.1.0&q=85",
  data: "https://images.unsplash.com/photo-1600528667634-9cf618cd6488?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjB0ZWFtJTIwY29sbGFib3JhdGlvbiUyMG1vZGVybiUyMG9mZmljZSUyMGdsYXNzJTIwd2FsbHN8ZW58MHx8fHwxNzczNzc1OTMxfDA&ixlib=rb-4.1.0&q=85"
};

const servicesData = {
  staffing: {
    title: "IT Staffing & Talent Solutions",
    subtitle: "Access top-tier IT professionals through our AI-powered talent matching platform",
    icon: Users,
    image: IMAGES.staffing,
    primary: true,
    description: "Our IT staffing solutions combine decades of recruitment expertise with cutting-edge AI technology to deliver the right talent, faster. Whether you need contract professionals, permanent hires, or executive search services, we've placed over 5,000 consultants at Fortune 500 companies.",
    features: [
      { title: "Contract Staffing", description: "Flexible IT professionals for short and long-term projects" },
      { title: "Permanent Placement", description: "Full-time hires vetted for technical and cultural fit" },
      { title: "Executive Search", description: "C-level and senior technology leadership recruitment" },
      { title: "Bench Management", description: "Access our ready-to-deploy talent pool instantly" },
      { title: "Corp-to-Corp (C2C)", description: "Seamless engagement with independent consultants" },
      { title: "AI-Powered Matching", description: "50% faster placements with our ML-driven matching" }
    ],
    stats: [
      { value: "5000+", label: "Consultants Placed" },
      { value: "50%", label: "Faster Placements" },
      { value: "98%", label: "Client Retention" },
      { value: "15+", label: "Industries Served" }
    ],
    technologies: ["Java", "Python", ".NET", "Cloud", "DevOps", "Data Engineering", "QA", "SAP", "Salesforce", "Cybersecurity"]
  },
  "ai-automation": {
    title: "AI & Automation Solutions",
    subtitle: "Transform operations with intelligent automation and custom AI solutions",
    icon: Cpu,
    image: IMAGES.ai,
    primary: true,
    description: "Leverage the power of artificial intelligence and intelligent automation to streamline processes, reduce costs, and unlock new capabilities. Our AI solutions team brings deep expertise in machine learning, natural language processing, and robotic process automation.",
    features: [
      { title: "Process Automation (RPA)", description: "Automate repetitive tasks and workflows" },
      { title: "Machine Learning Integration", description: "Custom ML models for prediction and optimization" },
      { title: "AI Strategy Consulting", description: "Roadmap for AI adoption and implementation" },
      { title: "Intelligent Document Processing", description: "Extract insights from unstructured data" },
      { title: "Conversational AI", description: "Chatbots and virtual assistants for customer service" },
      { title: "Predictive Analytics", description: "Data-driven forecasting and decision support" }
    ],
    stats: [
      { value: "60%", label: "Cost Reduction" },
      { value: "10x", label: "Faster Processing" },
      { value: "85%", label: "Accuracy Improvement" },
      { value: "24/7", label: "Automated Operations" }
    ],
    technologies: ["Python", "TensorFlow", "PyTorch", "UiPath", "Automation Anywhere", "Azure AI", "AWS ML", "OpenAI", "LangChain"]
  },
  consulting: {
    title: "IT Consulting",
    subtitle: "Strategic technology guidance for enterprise growth and digital transformation",
    icon: Settings,
    image: IMAGES.consulting,
    primary: false,
    description: "Our IT consulting services help enterprises navigate complex technology landscapes, make informed decisions, and execute successful digital transformations. We bring industry expertise and proven methodologies to every engagement.",
    features: [
      { title: "Digital Transformation", description: "End-to-end modernization strategy and execution" },
      { title: "Technology Assessment", description: "Evaluate and optimize your technology stack" },
      { title: "Architecture Design", description: "Scalable, resilient system architectures" },
      { title: "IT Strategy", description: "Align technology investments with business goals" },
      { title: "Vendor Selection", description: "Evaluate and select the right technology partners" },
      { title: "Program Management", description: "Deliver complex initiatives on time and budget" }
    ],
    stats: [
      { value: "200+", label: "Enterprise Clients" },
      { value: "95%", label: "Project Success Rate" },
      { value: "20+", label: "Years Experience" },
      { value: "15+", label: "Industries" }
    ],
    technologies: ["Enterprise Architecture", "TOGAF", "Agile", "SAFe", "ITIL", "Cloud Strategy", "Security", "Compliance"]
  },
  "cloud-devops": {
    title: "Cloud & DevOps",
    subtitle: "Modernize infrastructure and accelerate software delivery",
    icon: Cloud,
    image: IMAGES.cloud,
    primary: false,
    description: "Accelerate your cloud journey and optimize software delivery with our Cloud & DevOps services. We help enterprises migrate to cloud, implement DevOps practices, and build modern, scalable infrastructure.",
    features: [
      { title: "Cloud Migration", description: "Lift-and-shift, re-platform, or re-architect to cloud" },
      { title: "DevOps Implementation", description: "CI/CD pipelines, automation, and best practices" },
      { title: "Infrastructure as Code", description: "Terraform, CloudFormation, and Ansible automation" },
      { title: "Container Orchestration", description: "Kubernetes, Docker, and microservices architecture" },
      { title: "Cloud Cost Optimization", description: "Reduce cloud spend without sacrificing performance" },
      { title: "Site Reliability Engineering", description: "Build resilient, observable systems" }
    ],
    stats: [
      { value: "40%", label: "Cost Reduction" },
      { value: "10x", label: "Deployment Frequency" },
      { value: "99.99%", label: "Uptime SLA" },
      { value: "60%", label: "Faster Time-to-Market" }
    ],
    technologies: ["AWS", "Azure", "GCP", "Kubernetes", "Docker", "Terraform", "Jenkins", "GitLab CI", "ArgoCD", "Prometheus"]
  },
  "data-analytics": {
    title: "Data & Analytics",
    subtitle: "Turn data into actionable business insights",
    icon: BarChart3,
    image: IMAGES.data,
    primary: false,
    description: "Unlock the value hidden in your data with our comprehensive Data & Analytics services. From data engineering to business intelligence, we help enterprises become truly data-driven organizations.",
    features: [
      { title: "Data Engineering", description: "Build scalable data pipelines and platforms" },
      { title: "Business Intelligence", description: "Dashboards and reports for decision-making" },
      { title: "Data Warehouse Modernization", description: "Migrate to modern cloud data platforms" },
      { title: "Advanced Analytics", description: "Statistical analysis and predictive modeling" },
      { title: "Data Governance", description: "Ensure data quality, security, and compliance" },
      { title: "Real-time Analytics", description: "Streaming data processing and insights" }
    ],
    stats: [
      { value: "300%", label: "ROI on Data Investments" },
      { value: "70%", label: "Faster Reporting" },
      { value: "50%", label: "Better Decision Accuracy" },
      { value: "PB+", label: "Data Managed" }
    ],
    technologies: ["Snowflake", "Databricks", "BigQuery", "Redshift", "Spark", "Airflow", "dbt", "Tableau", "Power BI", "Looker"]
  }
};

export default function ServicesPage() {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="pt-20">
        <div className="container-padding py-20 text-center">
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">Service Not Found</h1>
          <p className="text-slate-600 mb-8">The requested service page doesn't exist.</p>
          <Link to="/services/staffing">
            <Button className="btn-primary">View Our Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <div className="pt-20">
      <PageHeader 
        title={service.title}
        subtitle={service.subtitle}
        breadcrumbs={[
          { label: "Services", href: "/" },
          { label: service.title }
        ]}
      />

      {/* Overview */}
      <section className="section-padding bg-white" data-testid="overview-section">
        <div className="container-padding">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-[#2563EB]" />
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {service.description}
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                {service.stats.map((stat, index) => (
                  <div key={index} className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              <Link to="/contact?type=submit_requirement">
                <Button className="btn-primary" data-testid="cta-discuss-btn">
                  Discuss Your Needs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src={service.image}
                alt={service.title}
                className="rounded-2xl shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#2563EB]/10 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="features-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              What We Offer
            </div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our {service.title.split(' ')[0]} Capabilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {service.features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-100 hover:shadow-lg transition-shadow"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="section-padding bg-white" data-testid="technologies-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Technologies & Expertise
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {service.technologies.map((tech, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-slate-100 text-[#0A2540] rounded-full font-medium hover:bg-[#2563EB] hover:text-white transition-colors cursor-default"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A2540]" data-testid="cta-section">
        <div className="container-padding text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how our {service.title.toLowerCase()} can help transform your business.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?type=hire_talent">
              <Button className="btn-accent text-base" data-testid="cta-hire-btn">
                Hire Talent
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A2540] rounded-full px-8 py-4 text-base font-medium"
                data-testid="cta-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
